import React, {useEffect} from "react";
import { useDispatch } from "react-redux";
import { KButton } from "@kyburz/kdesign-system";
import { useHistory, useRouteMatch } from "react-router-dom";
import { setAlert } from "../../../redux/actions/actions";
import { useStyles } from "../../smsCommands/styles";
import useSetState from "../../../hooks/useTypedSetState";
import BaseLayout from "../../../components/layouts/BaseLayout";
import {
  useCreateIoTKeyCommand,
  useGetIoTKeyCommand,
  useUpdateIoTKeyCommand
} from "../../../apiCalls/config/iotKeyCommands";
import IoTKeyCommandEditor from "../IoTKeyCommandEditor/IoTKeyCommandEditor";

interface RouteParams {
  id: string;
}

interface Props {
  edit?: boolean;
}

const CreateEditIoTKeyCommandPage = ({
  edit,
}: Props) => {
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();

  const match = useRouteMatch({
    path: "/config/iot_commands/:id",
    strict: true,
    sensitive: true,
  });
  const { id } = match?.params as RouteParams;

  const { request: getIoTKeyCommand } = useGetIoTKeyCommand();
  const { loading: saveLoading, request: createIoTKeyCommand } =
    useCreateIoTKeyCommand();
  const { request: updateIoTKeyCommand } = useUpdateIoTKeyCommand();

  const title = edit ? "Edit IoT Key Command" : "Create New IoT Key Command";
  const goBack = () => history.push("/config/iot_commands");

  const [iotKeyCommand, setIoTKeyCommand] = useSetState({
    id: "",
    name: "",
    command: "",
    companies: [],
    parameters: {
      additionalProp1: "",
      additionalProp2: "",
      additionalProp3: "",
    }
  });

  useEffect(() => {
    if (edit && id !== undefined) {
      getIoTKeyCommand(id).then((res: any) => {
        setIoTKeyCommand({
          id,
          name: res?.name,
          command: res?.command,
          companies: res?.companies,
          parameters: res?.parameters,
        });
      });
    }
  }, [edit, id]);

  const createNewSMSCommand = () => {
    createIoTKeyCommand(iotKeyCommand)
      .then(() => {
        dispatch(
          setAlert(
            `SMS Command '${iotKeyCommand.name}' has been saved`,
            "success",
          ),
        );
        goBack();
      })
      .catch((err) => {
        dispatch(setAlert(err.message || "An error occurred", "error"));
        console.error(err);
      });
  };

  const updateIoTKey = () => {
    updateIoTKeyCommand(iotKeyCommand)
      .then(() => {
        dispatch(
          setAlert(
            `SMS Command '${iotKeyCommand.name}' has been saved`,
            "success",
          ),
        );
        goBack();
      })
      .catch((err) => {
        dispatch(setAlert(err.message || "An error occurred", "error"));
        console.error(err);
      });
  };

  const onSave = () => {
    if (edit) {
      updateIoTKey();
      return;
    }
    createNewSMSCommand();
  };

  return (
    <BaseLayout
      title={title}
      handleArrowClick={goBack}
      mainClassname={classes.layout}
    >
      <IoTKeyCommandEditor
        IoTKeyCommand={{ ...iotKeyCommand, id: undefined }}
        onIoTKeyCommandChanged={setIoTKeyCommand}
      />
      <footer className={classes.buttons}>
        <KButton children="Cancel" theme="secondary" onClick={goBack} />
        <KButton
          children="Save and close"
          onClick={onSave}
          loading={saveLoading}
        />
      </footer>
    </BaseLayout>
  );
};

export default CreateEditIoTKeyCommandPage;
